import React, { useState, useEffect } from 'react';
import '../styles/ContactImport.css';
import {
  importContacts, addManualContact, listContacts, removeContact, getContactsAuthUrl,
} from '../api/contacts';

// Minimal CSV parser (handles quoted fields + commas/newlines inside quotes).
function parseCSV(text) {
  const t = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  const rows = [];
  let field = '', row = [], inQ = false;
  for (let i = 0; i < t.length; i++) {
    const c = t[i];
    if (inQ) {
      if (c === '"') { if (t[i + 1] === '"') { field += '"'; i++; } else inQ = false; }
      else field += c;
    } else if (c === '"') inQ = true;
    else if (c === ',') { row.push(field); field = ''; }
    else if (c === '\n') { row.push(field); rows.push(row); row = []; field = ''; }
    else field += c;
  }
  if (field.length || row.length) { row.push(field); rows.push(row); }
  return rows.filter((r) => r.some((v) => v && v.trim()));
}

// Turn Google/Outlook CSV rows into { name, email }. Works across export formats
// by matching columns loosely: any "mail" column is an email; name is "Name",
// "Display Name", or First + Last.
function csvToContacts(text) {
  const rows = parseCSV(text);
  if (rows.length < 2) return [];
  const header = rows[0].map((h) => h.trim().toLowerCase());
  const emailCols = header.map((h, i) => (h.includes('mail') ? i : -1)).filter((i) => i >= 0);
  const nameCol = header.findIndex((h) => h === 'name' || h === 'display name' || h === 'full name');
  const firstCol = header.findIndex((h) => h.includes('first name'));
  const lastCol = header.findIndex((h) => h.includes('last name'));

  const seen = new Set();
  const out = [];
  for (let r = 1; r < rows.length; r++) {
    const cells = rows[r];
    let name = '';
    if (nameCol >= 0) name = (cells[nameCol] || '').trim();
    if (!name && (firstCol >= 0 || lastCol >= 0)) {
      name = [cells[firstCol], cells[lastCol]].filter(Boolean).join(' ').trim();
    }
    for (const ci of emailCols) {
      const raw = (cells[ci] || '').trim();
      // some exports put multiple emails in one cell separated by ::: or ;
      for (const e of raw.split(/[;:,]+/).map((s) => s.trim())) {
        const email = e.toLowerCase();
        if (email.includes('@') && !seen.has(email)) { seen.add(email); out.push({ name, email }); }
      }
    }
  }
  return out;
}

function ContactImport() {
  const [contacts, setContacts] = useState([]);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);
  const [showAddManual, setShowAddManual] = useState(false);
  const [manualEmail, setManualEmail] = useState('');
  const [manualName, setManualName] = useState('');

  const loadContacts = async () => {
    try {
      const result = await listContacts();
      setContacts(result.contacts || []);
    } catch (err) { console.error('Failed to load contacts:', err); }
  };

  useEffect(() => {
    loadContacts();
    // Handle return from an OAuth import (?contacts=google&count=N)
    try {
      const p = new URLSearchParams(window.location.search);
      const c = p.get('contacts');
      if (c === 'google' || c === 'outlook') {
        setNotice(`Imported ${p.get('count') || 0} contacts from ${c === 'google' ? 'Google' : 'Outlook'}.`);
        loadContacts();
      } else if (c === 'error') {
        setError('That import didn’t complete. You can try again, or use CSV below.');
      }
      if (c) window.history.replaceState({}, '', window.location.pathname);
    } catch (e) { /* ignore */ }
  }, []);

  const connect = async (provider) => {
    setError(''); setNotice('');
    try {
      const { url } = await getContactsAuthUrl(provider);
      window.location.href = url;
    } catch (err) {
      setError(err.message || 'This import isn’t available yet.');
    }
  };

  const onFile = async (e) => {
    setError(''); setNotice('');
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const text = await file.text();
      const parsed = csvToContacts(text);
      if (parsed.length === 0) {
        setError('No email addresses found in that file. Make sure it’s a Google or Outlook contacts CSV.');
        return;
      }
      const res = await importContacts('csv', parsed);
      setNotice(`Found ${parsed.length} in the file — added ${res.importedCount} new contact${res.importedCount === 1 ? '' : 's'}.`);
      await loadContacts();
    } catch (err) {
      setError(err.message || 'Could not read that file.');
    } finally {
      setBusy(false);
      e.target.value = '';
    }
  };

  const handleAddManual = async (ev) => {
    ev.preventDefault();
    setError(''); setNotice('');
    try {
      await addManualContact(manualEmail, manualName);
      setManualEmail(''); setManualName(''); setShowAddManual(false);
      await loadContacts();
    } catch (err) { setError(err.message || 'Failed to add contact'); }
  };

  const remove = async (id) => {
    try { await removeContact(id); setContacts((p) => p.filter((c) => c.id !== id)); }
    catch (err) { setError(err.message || 'Could not remove'); }
  };

  return (
    <div className="contact-import-container">
      <div className="import-section">
        <h3>📬 Import your contacts</h3>
        <p>Bring in people you already know. The fastest way is a CSV export.</p>

        <div className="import-buttons">
          <button className="import-btn google" onClick={() => connect('google')} disabled={busy}>
            🔍 Connect Google
          </button>
          <button className="import-btn outlook" onClick={() => connect('microsoft')} disabled={busy}>
            🪟 Connect Outlook
          </button>
        </div>

        <div className="csv-row">
          <label className="csv-upload">
            📄 Upload CSV (Google / Outlook export)
            <input type="file" accept=".csv,text/csv" onChange={onFile} disabled={busy} hidden />
          </label>
          <details className="csv-help">
            <summary>How do I get the CSV?</summary>
            <ul>
              <li><strong>Google:</strong> contacts.google.com → Export → Google CSV.</li>
              <li><strong>Outlook:</strong> Outlook → People → Manage → Export contacts → CSV.</li>
            </ul>
          </details>
        </div>

        {notice && <div className="send-notice">{notice}</div>}
        {error && <div className="error-message">{error}</div>}
      </div>

      <div className="manual-section">
        <div className="manual-header">
          <h3>➕ Add manually</h3>
          <button className="toggle-btn" onClick={() => setShowAddManual(!showAddManual)}>
            {showAddManual ? '✖' : '+'}
          </button>
        </div>
        {showAddManual && (
          <form onSubmit={handleAddManual} className="manual-form">
            <input type="email" value={manualEmail} onChange={(e) => setManualEmail(e.target.value)} placeholder="Email address" required />
            <input type="text" value={manualName} onChange={(e) => setManualName(e.target.value)} placeholder="Name (optional)" />
            <button type="submit" className="btn btn-primary">Add contact</button>
          </form>
        )}
      </div>

      <div className="contacts-list">
        <h3>Your contacts ({contacts.length})</h3>
        {contacts.length === 0 ? (
          <p className="empty-state">No contacts yet. Import a CSV or add one manually.</p>
        ) : (
          <table className="contacts-table">
            <thead>
              <tr><th>Name</th><th>Email</th><th>Source</th><th></th></tr>
            </thead>
            <tbody>
              {contacts.map((contact) => (
                <tr key={contact.id}>
                  <td>{contact.contact_name}</td>
                  <td>{contact.contact_email}</td>
                  <td><span className="source-badge">{contact.imported_from}</span></td>
                  <td><button className="btn-small" onClick={() => remove(contact.id)}>Remove</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="info-box">
        <h4>🔒 Your contacts stay yours</h4>
        <p>Imported contacts are private to you. A contact only ever becomes a connection when both sides say yes.</p>
      </div>
    </div>
  );
}

export default ContactImport;
